'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { z } from '@repo/schema';
import { GetXeroItemSchema } from '@repo/schema/invoice';
import { useQuery } from '@repo/tanstack/query';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, selectVariant, Spinner } from '@repo/ui/components';
import { cn, VariantProps } from '@repo/ui/lib/utils';
import { ComponentProps, ReactNode } from 'react';
import { ReadOnlyField, useReadOnly } from '../../components/readonly.tsx';
import { useFieldContext } from '../../lib/form-context.ts';
type XeroItem = z.infer<typeof GetXeroItemSchema>;
export const FormProductSelect = t0 => {
  const $ = _c(56);
  const {
    compact: t1,
    "data-testid": dataTestId,
    description,
    filterItem,
    isPending: t2,
    label,
    onSelectItem,
    organisationId,
    placeholder: t3,
    variant: t4
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const isPending = t2 === undefined ? false : t2;
  const placeholder = t3 === undefined ? "" : t3;
  const variant = t4 === undefined ? "default" : t4;
  const field = useFieldContext();
  let t5;
  if ($[0] !== field.state.meta.errors) {
    t5 = field.state.meta.errors ?? [];
    $[0] = field.state.meta.errors;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const errors = t5;
  const api = useApi();
  let t6;
  if ($[2] !== api.organisations.xeroItems || $[3] !== organisationId) {
    t6 = api.organisations.xeroItems.queryOptions({
      path: {
        organisation: organisationId ?? 0
      }
    }, {
      enabled: !!organisationId
    });
    $[2] = api.organisations.xeroItems;
    $[3] = organisationId;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    data: t7,
    error,
    isLoading
  } = useQuery(t6);
  const items = t7 === undefined ? [] : t7;
  let t8;
  if ($[5] !== filterItem) {
    t8 = item => !!item.code && (filterItem ? filterItem(item) : true);
    $[5] = filterItem;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const selectableItems = items.filter(t8);
  const handleChange = value => {
    field.handleChange(value);
    onSelectItem?.(selectableItems.find(item_0 => item_0.code === value));
  };
  const loading = isPending || isLoading;
  const hasItems = selectableItems.length > 0;
  const showEmptyMessage = !loading && (!!error || !hasItems);
  const isReadOnly = useReadOnly();
  let t9;
  if ($[7] !== field.name || $[8] !== label) {
    t9 = label && typeof label === "string" ? <FieldLabel htmlFor={field.name}>{label}</FieldLabel> : label;
    $[7] = field.name;
    $[8] = label;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const fieldLabel = t9;
  if (isReadOnly) {
    const t10 = selectableItems.find(item_1 => item_1.code === field.state.value)?.name ?? "-";
    let t11;
    if ($[10] !== fieldLabel || $[11] !== t10) {
      t11 = <ReadOnlyField label={fieldLabel}>{t10}</ReadOnlyField>;
      $[10] = fieldLabel;
      $[11] = t10;
      $[12] = t11;
    } else {
      t11 = $[12];
    }
    return t11;
  }
  let t10;
  if ($[13] !== handleChange) {
    t10 = val => val && handleChange(val);
    $[13] = handleChange;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = field.state.value;
  const t12 = compact && "flex justify-between gap-2 p-0 text-base data-[size=default]:h-6";
  let t13;
  if ($[15] !== t12) {
    t13 = cn("w-full", t12);
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const t14 = dataTestId ?? "form-role-select-trigger";
  const t15 = !hasItems;
  let t16;
  if ($[17] !== placeholder || $[18] !== selectableItems) {
    t16 = value_0 => selectableItems.find(item_2 => item_2.code === value_0)?.name ?? placeholder;
    $[17] = placeholder;
    $[18] = selectableItems;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== placeholder || $[21] !== t16) {
    t17 = <SelectValue placeholder={placeholder} data-sentry-element="SelectValue" data-sentry-source-file="form-product-select.tsx">{t16}</SelectValue>;
    $[20] = placeholder;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== loading) {
    t18 = loading && <Spinner className="text-muted-foreground ml-auto size-4" />;
    $[23] = loading;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== field.name || $[26] !== t13 || $[27] !== t14 || $[28] !== t15 || $[29] !== t17 || $[30] !== t18 || $[31] !== variant) {
    t19 = <SelectTrigger className={t13} data-testid={t14} disabled={t15} id={field.name} variant={variant} data-sentry-element="SelectTrigger" data-sentry-source-file="form-product-select.tsx">{t17}{t18}</SelectTrigger>;
    $[25] = field.name;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t17;
    $[30] = t18;
    $[31] = variant;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== selectableItems) {
    t20 = selectableItems.map(_temp);
    $[33] = selectableItems;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t20) {
    t21 = <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="form-product-select.tsx">{t20}</SelectContent>;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== field.state.value || $[38] !== t10 || $[39] !== t19 || $[40] !== t21) {
    t22 = <div className="relative"><Select onValueChange={t10} value={t11} data-sentry-element="Select" data-sentry-source-file="form-product-select.tsx">{t19}{t21}</Select></div>;
    $[37] = field.state.value;
    $[38] = t10;
    $[39] = t19;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== showEmptyMessage) {
    t23 = showEmptyMessage && <FieldDescription>No Xero items available</FieldDescription>;
    $[42] = showEmptyMessage;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== description || $[45] !== showEmptyMessage) {
    t24 = !showEmptyMessage && description && typeof description === "string" && <FieldDescription>{description}</FieldDescription>;
    $[44] = description;
    $[45] = showEmptyMessage;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  const t25 = !showEmptyMessage && description && typeof description !== "string" && description;
  let t26;
  if ($[47] !== errors) {
    t26 = <FieldError className="text-sm" errors={errors} data-sentry-element="FieldError" data-sentry-source-file="form-product-select.tsx" />;
    $[47] = errors;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== fieldLabel || $[50] !== t22 || $[51] !== t23 || $[52] !== t24 || $[53] !== t25 || $[54] !== t26) {
    t27 = <Field className="gap-1" data-sentry-element="Field" data-sentry-component="FormProductSelect" data-sentry-source-file="form-product-select.tsx"><FieldContent data-sentry-element="FieldContent" data-sentry-source-file="form-product-select.tsx">{fieldLabel}{t22}{t23}{t24}{t25}{t26}</FieldContent></Field>;
    $[49] = fieldLabel;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  return t27;
};
function _temp(item_3) {
  return <SelectItem data-testid={`form-product-select-option-${item_3.code}`} key={item_3.code} value={item_3.code}>{item_3.name ?? item_3.code}</SelectItem>;
}